import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../services/api';
import MeetingForm, { Meeting } from '../components/MeetingForm';
import AttendanceModal from '../components/AttendanceModal';
import TableFilter from '../components/TableFilter';
import ActionButtons from '../components/ActionButtons';
import DetailsPanel from '../components/DetailsPanel';
import ReportModal from '../components/ReportModal';

const MeetingsPage: React.FC = () => {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [filteredMeetings, setFilteredMeetings] = useState<Meeting[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<Meeting | null>(null);
  const [showMinutesModal, setShowMinutesModal] = useState(false);
  const [selectedMeetingForMinutes, setSelectedMeetingForMinutes] = useState<Meeting | null>(null);
  const [minutesText, setMinutesText] = useState('');
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrLink, setQrLink] = useState('');
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [selectedMeetingForAttendance, setSelectedMeetingForAttendance] = useState<Meeting | null>(null);

  // Modal de Relatórios
  const [reportModal, setReportModal] = useState<{ isOpen: boolean; rowId?: number; isRowReport: boolean }>({
    isOpen: false,
    isRowReport: false
  });

  // Master-Detail State
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);

  const fetchMeetings = useCallback(async () => {
    try {
      const response = await api.get('/meetings/');
      setMeetings(response.data);
      setFilteredMeetings(response.data);
    } catch (error) {
      console.error("Error fetching meetings", error);
    }
  }, []);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  const handleFilter = (filters: { [key: string]: string }) => {
    let result = meetings;

    if (filters.title) {
      result = result.filter(m => m.title.toLowerCase().includes(filters.title.toLowerCase()));
    }
    if (filters.date) {
      result = result.filter(m => m.start_time.startsWith(filters.date));
    }

    setFilteredMeetings(result);
  };

  const handleSuccess = () => {
    setShowForm(false);
    setEditingMeeting(null);
    fetchMeetings();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingMeeting(null);
  };

  const handleEdit = (meeting: Meeting) => {
    setEditingMeeting(meeting);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir esta reunião?')) {
      try {
        await api.delete(`/meetings/${id}/`);
        fetchMeetings();
        if (selectedMeeting?.id === id) setSelectedMeeting(null);
      } catch (error) {
        console.error("Error deleting meeting", error);
        alert('Erro ao excluir reunião.');
      }
    }
  };

  const handleOpenMinutes = (meeting: Meeting) => {
    setSelectedMeetingForMinutes(meeting);
    setMinutesText(meeting.minutes || '');
    setShowMinutesModal(true);
  };

  const handleSaveMinutes = async () => {
    if (!selectedMeetingForMinutes || !selectedMeetingForMinutes.id) return;
    try {
      await api.patch(`/meetings/${selectedMeetingForMinutes.id}/register_minutes/`,
        { minutes: minutesText }
      );
      setShowMinutesModal(false);
      fetchMeetings();
      // Update selected meeting if open
      if (selectedMeeting?.id === selectedMeetingForMinutes.id) {
        setSelectedMeeting({ ...selectedMeeting, minutes: minutesText });
      }
    } catch (error) {
      console.error("Error saving minutes", error);
      alert('Erro ao salvar ata.');
    }
  };

  const handleShowQR = (meeting: Meeting) => {
    const link = `${window.location.origin}/checkin/meeting/${meeting.id}`;
    setQrLink(link);
    setShowQRModal(true);
  };

  const handleOpenAttendance = (meeting: Meeting) => {
    setSelectedMeetingForAttendance(meeting);
    setShowAttendanceModal(true);
  };

  const handlePrintQR = () => {
    const printWindow = window.open('', '', 'width=600,height=600');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>QR Code - Check-in</title>
            <style>
              body { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; font-family: sans-serif; }
              h1 { margin-bottom: 20px; }
              img { width: 400px; height: 400px; }
              p { margin-top: 20px; font-size: 1.2rem; }
            </style>
          </head>
          <body>
            <h1>Check-in de Presença</h1>
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(qrLink)}" />
            <p>${qrLink}</p>
            <script>
              window.onload = function() { window.print(); window.close(); }
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Pending': return <span className="badge badge-warning">Pendente</span>;
      case 'Completed': return <span className="badge badge-success">Concluída</span>;
      case 'Canceled': return <span className="badge badge-danger">Cancelada</span>;
      case 'Postponed': return <span className="badge badge-info">Adiada</span>;
      default: return <span className="badge badge-secondary">{status}</span>;
    }
  };

  const openGeneralReports = () => setReportModal({ isOpen: true, isRowReport: false });
  const openRowReport = (id: number) => setReportModal({ isOpen: true, rowId: id, isRowReport: true });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1>Reuniões / Encontros</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Gerencie os encontros com os pais</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary" onClick={openGeneralReports}>Relatórios de Reuniões</button>
          {!showForm && (
            <button className="btn" onClick={() => setShowForm(true)}>
              + Nova Reunião
            </button>
          )}
        </div>
      </div>

      {showForm ? (
        <div style={{ marginBottom: '30px' }}>
          <MeetingForm
            onSuccess={handleSuccess}
            onCancel={handleCancel}
            meetingToEdit={editingMeeting}
          />
        </div>
      ) : (
        <TableFilter
          filters={[
            { key: 'title', label: 'Título' },
            { key: 'date', label: 'Data', type: 'date' }
          ]}
          onFilter={handleFilter}
        />
      )}

      {/* Modais */}
      {showMinutesModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div className="card" style={{ width: '500px', maxWidth: '90%' }}>
            <h3>Registro de Ata/Conversa</h3>
            <p>Reunião: <strong>{selectedMeetingForMinutes?.title}</strong></p>
            <textarea
              value={minutesText}
              onChange={(e) => setMinutesText(e.target.value)}
              rows={10}
              style={{ width: '100%', marginBottom: '20px' }}
              placeholder="Descreva o que foi conversado..."
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowMinutesModal(false)}>Cancelar</button>
              <button className="btn" onClick={handleSaveMinutes}>Salvar Ata</button>
            </div>
          </div>
        </div>
      )}

      {showQRModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div className="card" style={{ width: '400px', maxWidth: '90%', textAlign: 'center', padding: '40px' }}>
            <h3 style={{ marginBottom: '20px' }}>Check-in de Presença</h3>
            <div style={{ background: 'white', padding: '20px', display: 'inline-block', borderRadius: '8px' }}>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(qrLink)}`}
                alt="QR Code"
                style={{ width: '250px', height: '250px' }}
              />
            </div>
            <p style={{ marginTop: '20px', wordBreak: 'break-all', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              {qrLink}
            </p>
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button className="btn" onClick={handlePrintQR}>Imprimir</button>
              <button className="btn btn-secondary" onClick={() => setShowQRModal(false)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {showAttendanceModal && selectedMeetingForAttendance && selectedMeetingForAttendance.id && (
        <AttendanceModal
          type="meetings"
          itemId={selectedMeetingForAttendance.id}
          title={selectedMeetingForAttendance.title}
          onClose={() => setShowAttendanceModal(false)}
        />
      )}

      {reportModal.isOpen && (
        <ReportModal 
          isOpen={reportModal.isOpen}
          onClose={() => setReportModal({ ...reportModal, isOpen: false })}
          category="Meeting"
          rowId={reportModal.rowId}
          isRowReport={reportModal.isRowReport}
        />
      )}

      {/* Master-Detail Layout */}
      <div className="page-container">
        <div className="list-container card" style={{ overflowX: 'auto', padding: 0 }}>
          <table>
            <thead>
              <tr>
                <th>Título</th>
                <th>Início</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredMeetings.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '30px', color: 'var(--text-light)' }}>
                    Nenhuma reunião encontrada.
                  </td>
                </tr>
              ) : (
                filteredMeetings.map((meeting) => (
                  <tr
                    key={meeting.id}
                    onClick={() => setSelectedMeeting(meeting)}
                    className={selectedMeeting?.id === meeting.id ? 'selected' : ''}
                  >
                    <td style={{ fontWeight: '500' }}>{meeting.title}</td>
                    <td>{formatDateTime(meeting.start_time)}</td>
                    <td>{getStatusBadge(meeting.status || 'Pending')}</td>
                    <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      <ActionButtons
                        onQrCode={() => handleShowQR(meeting)}
                        onAttendance={() => handleOpenAttendance(meeting)}
                        onMinutes={() => handleOpenMinutes(meeting)}
                        onReport={() => meeting.id && openRowReport(meeting.id)}
                        onEdit={() => handleEdit(meeting)}
                        onDelete={() => meeting.id && handleDelete(meeting.id)}
                        deletePermission="delete_meeting"
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {selectedMeeting && (
          <DetailsPanel title="Detalhes da Reunião" onClose={() => setSelectedMeeting(null)}>
            <div className="details-row">
              <div className="details-label">Título</div>
              <div className="details-value">{selectedMeeting.title}</div>
            </div>
            <div className="details-row">
              <div className="details-label">Data e Hora</div>
              <div className="details-value">
                {formatDateTime(selectedMeeting.start_time)} - {new Date(selectedMeeting.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
            <div className="details-row">
              <div className="details-label">Responsável</div>
              <div className="details-value">{selectedMeeting.responsible_name || '-'}</div>
            </div>
            <div className="details-row">
              <div className="details-label">Status</div>
              <div className="details-value">{getStatusBadge(selectedMeeting.status || 'Pending')}</div>
            </div>
            <div className="details-row">
              <div className="details-label">Descrição</div>
              <div className="details-value">{selectedMeeting.description || 'Sem descrição.'}</div>
            </div>
            <div className="details-row">
              <div className="details-label">Ata / Anotações</div>
              <div className="details-value" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                {selectedMeeting.minutes || 'Nenhuma ata registrada.'}
              </div>
            </div>
            <div className="details-row">
              <div className="details-label">Participação</div>
              <div className="details-value">
                {selectedMeeting.invited_parents_count ? (
                  <span className="badge badge-info">{selectedMeeting.invited_parents_count} convidados</span>
                ) : (
                  <span className="badge badge-secondary">Todos convidados</span>
                )}
              </div>
            </div>
          </DetailsPanel>
        )}
      </div>
    </div>
  );
};

export default MeetingsPage;
